import { useEffect, useState } from 'react';
import {
  dsldUrl,
  parseDsld,
  parseRxTerms,
  rxTermsUrl,
  type MedicationKind,
  type MedicationSuggestion,
} from '@/lib/medicationSuggestions';

const MIN_CHARS = 2;
const DEBOUNCE_MS = 300;
const CACHE_MAX = 300;

/** Per-session cache keyed by kind + term (NLM asks integrators to cache). Insertion order doubles as FIFO eviction. */
const cache = new Map<string, MedicationSuggestion[]>();

function cachePut(key: string, value: MedicationSuggestion[]) {
  if (cache.size >= CACHE_MAX) {
    const oldest = cache.keys().next().value;
    if (oldest !== undefined) cache.delete(oldest);
  }
  cache.set(key, value);
}

/**
 * Debounced name suggestions for the Medications form (sources and terms in
 * lib/medicationSuggestions). Called from the browser directly, like the
 * address field's lookup: no key is involved and NLM's rate limit is per
 * client. A failed or slow lookup just yields no suggestions; the field is
 * free text either way, so the form never waits on these services.
 *
 * Pass `term = ''` to idle (no request, no suggestions).
 */
export function useMedicationSuggestions(kind: MedicationKind, term: string): MedicationSuggestion[] {
  const trimmed = term.trim();
  const key = `${kind}:${trimmed.toLowerCase()}`;
  const active = trimmed.length >= MIN_CHARS;

  const [result, setResult] = useState<{ key: string; kind: MedicationKind; items: MedicationSuggestion[] }>({
    key: '',
    kind,
    items: [],
  });

  useEffect(() => {
    if (!active || cache.has(key)) return undefined;

    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      try {
        const url = kind === 'supplement' ? dsldUrl(trimmed) : rxTermsUrl(trimmed);
        const resp = await fetch(url, { signal: controller.signal, headers: { Accept: 'application/json' } });
        if (!resp.ok) return;
        const data: unknown = await resp.json();
        const items = kind === 'supplement' ? parseDsld(data) : parseRxTerms(data);
        cachePut(key, items);
        setResult({ key, kind, items });
      } catch {
        // Aborted, offline, or blocked: no suggestions.
      }
    }, DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [active, key, kind, trimmed]);

  if (!active) return [];
  // While the next keystroke's lookup is pending, keep the last list of the
  // same kind on screen instead of closing it and reopening on every letter.
  return cache.get(key) ?? (result.kind === kind ? result.items : []);
}

/** Test hook: forget cached lookups between cases. */
export function clearMedicationSuggestionCache(): void {
  cache.clear();
}
